import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import { Formik, FieldArray } from 'formik';
import * as yup from 'yup';
import { TextFormattedNumber } from 'components';
import { shipOrderApi } from 'api';
import { carriers } from 'helpers/orders';
import OrderItemDetails from './OrderItemDetails';


const useStyles = makeStyles({
  root: {
    fontSize: '12px',
    color: '#434343',
  },
  rowBorder: {
    borderBottom: '1px solid #A9CEEE'
  },
  customPadding: {
    padding: '0 4%'
  },
  tab: {
    color: '#133C9A',
    borderBottom: '2px solid #133C9A',
    fontWeight: 600,
    marginBottom: '-1px'
  },
  title: {
    color: '#4277C6',
    textTransform: 'uppercase',
    fontSize: '12px',
    fontWeight: 600,
    marginBottom: '12px'
  },
  customInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff'
    }
  }
});

interface TrackErrors {
  track_number: string,
  title: string,
  carrier_code: string
}

interface ValidateOptions {
  index: number,
  from: Array<{
    value: {
      items: Array<{
        qty_ordered: number,
        qty_refunded: number,
        qty_to_ship: number,
        qty_shipped: number,
        is_virtual: number
      }>
    }
  }>
}

const OrderShipment = ({order, orderItemsDetails, setOpenShipment}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    entity_id,
    extension_attributes,
    shipping_description,
    shipping_amount,
    items
  } = order;

  const itemsToShip = items.reduce((acc, item) => {
    if (item.qty_invoiced === (item.qty_shipped + item.qty_refunded)) {
      return acc;
    }

    const itemWithQtyToShip = {
      ...item,
      qty_to_ship: item.qty_invoiced - item.qty_shipped - item.qty_refunded
    };

    return [...acc, itemWithQtyToShip];
  }, []);

  const shippingMethod = () => {
    const {method, total: {shipping_amount}} = extension_attributes.shipping_assignments[0].shipping;

    if (method === 'flatrate_flatrate') {
      return `${shipping_description} $${shipping_amount.toFixed(2)}`;
    }

    return shipping_description;
  };

  return (
    <Formik
      initialValues={{
        items: itemsToShip,
        notify: 0,
        appendComment: 1,
        comment: {
          is_visible_on_front: 1,
          comment: ''
        },
        tracks: [
          {
            track_number: '',
            title: '',
            carrier_code: ''
          }
        ]
      }}
      enableReinitialize
      validationSchema={
        yup.object({
          tracks: yup
            .array()
            .of(
              yup.object().shape({
                track_number: yup
                  .string()
                  .when('carrier_code', {
                    is: (val) => !!val,
                    then: yup.string().required('Field is required')
                  }),
                carrier_code: yup
                  .string()
                  .when('track_number', {
                    is: (val) => !!val,
                    then: yup.string().required('Field is required')
                  }),
              }, [['carrier_code', 'track_number']])
            ),
          items: yup
            .array()
            .of(
              yup.object().shape({
                qty_ordered: yup.number(),
                qty_to_ship: yup
                  .number()
                  .min(0)
                  .typeError('Invalid value')
                  .test('qty_to_ship', 'Not allowed qty', (value, ctx) => {
                    const itemIndex = (ctx.options as ValidateOptions)?.index;
                    const {
                      qty_ordered,
                      qty_shipped,
                      qty_refunded,
                      qty_to_ship,
                      is_virtual
                    } = (ctx.options as ValidateOptions).from[1].value.items[itemIndex];

                    const isQtyToShip = (ctx.options as ValidateOptions).from[1].value.items
                      .filter((item) => item?.qty_to_ship && item?.qty_to_ship !== 0);

                    if (!isQtyToShip.length) {
                      return false;
                    }

                    if (!qty_to_ship || is_virtual === 1) {
                      return true;
                    }

                    if (qty_ordered == qty_shipped || qty_ordered == qty_refunded) {
                      return false;
                    }

                    return (qty_refunded + qty_shipped + qty_to_ship) <= Number(qty_ordered);
                  })
              })
            )
        })
      }
      onSubmit={async (values) => {
        const {comment, tracks, ...rest} = values;
        const hasTrackDataValues = Object.values(tracks[0]).every((x) => !!x);
        const shipData = {
          ...rest,
          items: values.items.reduce((acc, item) => (
            item?.qty_to_ship
              ? [...acc, {order_item_id: item.item_id, qty: item.qty_to_ship}]
              : acc
          ), [])
        };

        if (!!comment.comment.length) {
          shipData['comment'] = comment;
        }

        if (hasTrackDataValues) {
          shipData['tracks'] = tracks;
        }

        const physicalItems = values.items.filter((item) => item.is_virtual === 0);
        const isOrderComplete = physicalItems.length > 0 && physicalItems.every((item) => {
          const qtyToShip = item?.qty_to_ship || 0;
          return qtyToShip + item.qty_shipped === item.qty_ordered
            || qtyToShip + item.qty_shipped === item.qty_ordered - item.qty_refunded;
        });
        const isShipmentSuccess = await shipOrderApi({
          dispatch,
          entityId: entity_id,
          shipData,
          isOrderComplete
        });

        if (isShipmentSuccess) {
          setOpenShipment(false);
        }
      }}
    >
      {({
        values,
        touched,
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        setFieldTouched
      }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <div className={classes.root}>
              <div className={`flex items-center justify-between w-full ${classes.rowBorder} ${classes.customPadding}`}>
                <div className={`py-3 mx-4 w-full text-center ${classes.tab}`}>
                  Shipment & Tracking Information
                </div>
                <div className="w-full py-3 text-center font-bold">
                  {shippingMethod()}
                </div>
                <div className="w-full py-3 text-center">
                  {'Total Shipping Charges: '}
                  <span className="font-bold">
                    <TextFormattedNumber
                      value={shipping_amount}
                      prefix="$"
                    />
                  </span>
                </div>
              </div>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={`py-3 ${classes.customPadding}`}>
                      <span className="mx-4">Product</span>
                    </TableCell>
                    <TableCell className={`py-3 ${classes.customPadding}`} width="20%">
                      <span className="mx-4">Quantity</span>
                    </TableCell>
                    <TableCell className={`py-3 ${classes.customPadding}`} width="20%">
                      <span className="mx-4">Quantity to Ship</span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <FieldArray name="items">
                    {() => (
                      <>
                        {values.items.map((item, index) => {
                          const {
                            name,
                            qty_ordered,
                            qty_invoiced,
                            qty_refunded,
                            qty_shipped,
                            qty_canceled,
                            item_id,
                            is_virtual
                          } = item;

                          if (is_virtual == 1) {
                            return null;
                          }

                          const isError = (touched.items?.[index]?.qty_to_ship ||
                            touched.items?.[index]?.qty_shipped) &&
                            Boolean(errors.items?.[index]?.qty_to_ship);
                          const errorText = (touched.items?.[index]?.qty_to_ship ||
                            touched.items?.[index]?.qty_shipped) &&
                            errors.items?.[index]?.qty_to_ship;
                          return (
                            <TableRow key={item_id}>
                              <TableCell className={`py-3 ${classes.customPadding}`}>
                                <div className="mx-4 text-12">
                                  <div>{name}</div>
                                  <OrderItemDetails
                                    item={item}
                                    itemDetails={orderItemsDetails?.[index]}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className={`py-3 ${classes.customPadding}`}>
                                <div className="mx-4 text-12">
                                  <div>{`Ordered ${qty_ordered}`}</div>
                                  <div>{`Invoiced ${qty_invoiced}`}</div>
                                  {!!qty_refunded &&
                                  <div>{`Refunded ${qty_refunded}`}</div>
                                  }
                                  {!!qty_shipped &&
                                  <div>{`Shipped ${qty_shipped}`}</div>
                                  }
                                  {!!qty_canceled &&
                                  <div>{`Canceled ${qty_canceled}`}</div>
                                  }
                                </div>
                              </TableCell>
                              <TableCell className={`py-3 ${classes.customPadding}`}>
                                <TextField
                                  name={`items[${index}].qty_to_ship`}
                                  variant="outlined"
                                  className="ml-4"
                                  value={values.items[index]?.qty_to_ship || 0}
                                  inputProps={{
                                    className: 'text-right w-full p-2 text-12 bg-white'
                                  }}
                                  error={isError}
                                  helperText={errorText}
                                  onChange={({target: {value, name}}) => setFieldValue(name, +value)}
                                  onBlur={({target: {name}}) => setFieldTouched(name, true)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </>
                    )}
                  </FieldArray>
                </TableBody>
              </Table>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={`py-3 ${classes.customPadding}`}>
                      <span className="mx-4">Carrier</span>
                    </TableCell>
                    <TableCell className={`py-3 ${classes.customPadding}`}>
                      <span className="mx-4">Tracking Number</span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableCell className={`border-none py-3 ${classes.customPadding}`}>
                    <TextField
                      select
                      variant="outlined"
                      fullWidth
                      name="tracks[0].carrier_code"
                      className={`mx-4 ${classes.customInput}`}
                      value={values.tracks[0].carrier_code}
                      onChange={(event) => {
                        handleChange(event);
                        setFieldValue('tracks[0].title', event.target.value);
                      }}
                      onBlur={handleBlur}
                      error={touched.tracks?.[0]?.carrier_code && Boolean((errors.tracks?.[0] as TrackErrors)?.carrier_code)}
                      helperText={touched.tracks?.[0]?.carrier_code && (errors.tracks?.[0] as TrackErrors)?.carrier_code}
                    >
                      <MenuItem value="">{'---'}</MenuItem>
                      {carriers.map((carrier, index) => (
                        <MenuItem key={`carrier-${index}`} value={carrier.code}>{carrier.title}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell className={`border-none py-3 ${classes.customPadding}`}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="tracks[0].track_number"
                      className={`mx-4 ${classes.customInput}`}
                      value={values.tracks[0].track_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.tracks?.[0]?.track_number && Boolean((errors.tracks?.[0] as TrackErrors)?.track_number)}
                      helperText={touched.tracks?.[0]?.track_number && (errors.tracks?.[0] as TrackErrors)?.track_number}
                    />
                  </TableCell>
                  <TableRow>
                    <TableCell colSpan={2} className={`py-3 ${classes.customPadding}`}>
                      <div className={`mx-4 ${classes.title}`}>Shipment Comments</div>
                      <div>
                        <TextField
                          multiline
                          variant="outlined"
                          fullWidth
                          minRows="2"
                          className={`mx-4 ${classes.customInput}`}
                          name="comment.comment"
                          value={values.comment.comment}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                      <div className="mx-4">
                        <FormControlLabel
                          label="Email Copy of Shipment"
                          control={
                            <Checkbox
                              color="primary"
                              name="notify"
                              onChange={({target: {checked}}) => setFieldValue('notify', checked ? 1 : 0)}
                            />
                          }
                          className="mx-4 mt-2"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <div className={`py-3 mx-4 ${classes.customPadding}`}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setOpenShipment(false)}
                  className="uppercase mr-2 text-14 bg-light-dark"
                >
                  Back
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  type="submit"
                  className="uppercase mr-2 text-14"
                >
                  Submit Shipment
                </Button>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default OrderShipment;
